import { ReactText, useRef } from 'react'
import { useSelector } from 'react-redux'
import cx from 'classnames'

import { SharedRecipientSelector } from './RecipientSelector/SharedRecipientSelector'
import { useWorkspaceMembers } from 'hooks/useWorkspaceMembers'
import { useWorkspaceChannels } from 'hooks/useWorkspaceChannels'
import { UserValue, Value } from 'components/ShareSheet/RecipientSelector/Types'
import { Permission, RecipientsSelectorValueType, RootState, SettingsMember } from 'types'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { useAuth } from 'hooks/UseAuth'
import { DropdownOptions, RecipientSelectorProps } from './Types'
import { AccessDropdown } from './Access/AccessDropdown'
import Styles from './ShareSheet.module.scss'
import { Plus } from 'blaze-ui/icons'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useInformalFeedbackRequests } from 'hooks/useInformalFeedbackRequests'
import { InformalFeedbackRequestState } from 'api/clients/InformalFeedbackRequestsClient'
import { uniqBy } from 'lodash'

export const RecipientSelector = ({
  file,
  showLabel,
  accessDropdownVariant,
  accessDropdownOptions,
  className,
  secondaryAction,
  withSuggestedReviewers,
}: RecipientSelectorProps) => {
  const selectorRef = useRef<any>(null)
  const { userCanEdit, recipients, selectedPermission, setShareSheet, mainLayerEditors } = useShareSheetV2(file)
  const auth = useAuth()
  const isNotSelf = (member: SettingsMember) => auth?.id !== member.id
  const currentWorkspace = useSelector((state: RootState) =>
    state.workspace.workspaces.find((w) => w.id === state.workspace.currentWorkspaceId),
  )
  const currentWorkspaceId = currentWorkspace?.key

  const article = useCurrentArticleMeta()
  const { informalFeedbackRequests } = useInformalFeedbackRequests(article?.id || '', article?.main_layer.id || '', {
    states: [InformalFeedbackRequestState.Closed],
    enabled: !!article,
  })
  const workspaceMembers = useWorkspaceMembers(currentWorkspaceId, { includeInactive: false })
  const workspaceChannels = useWorkspaceChannels(currentWorkspaceId, true)
  const handleRecipientsSelectorChange = (value: Value[]) => {
    setShareSheet({ recipients: value })
  }
  const everyoneGroupAccess = {
    type: RecipientsSelectorValueType.EveryoneAtWorkspace,
    name: `Everyone at ${currentWorkspace?.name}`,
    avatar_url: currentWorkspace?.logo_url || '',
    member_count: workspaceMembers.length,
  }
  const childRows = workspaceMembers.map((m) => {
    return { type: RecipientsSelectorValueType.User, name: m.display_name, avatar_url: m.avatar_url || '' }
  })

  const buildLayerAccess = (memberCount: number) => {
    const layerHash = {
      type: RecipientsSelectorValueType.LayerEditors,
      name: 'Members who can edit main doc',
      avatar_url: '',
      member_count: memberCount,
    }

    return layerHash
  }

  const buildTopGroups = () => {
    const recipientTypes = recipients.map((r) =>
      r.type === RecipientsSelectorValueType.Company
        ? RecipientsSelectorValueType.EveryoneAtWorkspace
        : (r.type as RecipientsSelectorValueType),
    )

    if (file.type === 'layer') {
      return [
        {
          group: buildLayerAccess(mainLayerEditors.length),
          childRows: mainLayerEditors,
        },
        {
          group: everyoneGroupAccess,
          childRows: childRows,
        },
      ].filter((subject) => !recipientTypes.includes(subject['group']['type']))
    }

    if (recipientTypes.includes(RecipientsSelectorValueType.EveryoneAtWorkspace)) return []

    return [
      {
        group: everyoneGroupAccess,
        childRows: childRows,
      },
    ]
  }

  const lastReviewers = informalFeedbackRequests[0]?.reviews?.map((review: { reviewer: any }) => review.reviewer) || []
  const recipientUsers = recipients.filter(({ type }) => type === RecipientsSelectorValueType.User) as UserValue[]
  const recipientIds = recipientUsers.map((r) => r.value.id)
  const suggestedReviewers = uniqBy(
    lastReviewers.filter((reviewer) => !recipientIds.includes(reviewer.id)),
    'id',
  )
  const suggestedReviewersShown = secondaryAction ? suggestedReviewers.slice(0, 2) : suggestedReviewers.slice(0, 3)
  const showSuggestedReviewers = !!secondaryAction || (withSuggestedReviewers && suggestedReviewersShown.length > 0)

  let options = [Permission.Edit, Permission.Comment, Permission.Read]
  if (file.type === 'folder' || file.type === 'design') options = [Permission.Edit, Permission.Read]
  const accessOptions = accessDropdownOptions ?? options

  return (
    <>
      <div
        className={cx(Styles.recipientSelector, className, {
          [Styles.withPeople]: recipients.length > 0,
        })}
        onClick={() => {
          selectorRef?.current?.focus()
        }}
      >
        {showLabel && <span className={Styles.toLabel}>To:</span>}
        <SharedRecipientSelector
          ref={selectorRef}
          placeholder="Add people or emails"
          dropdownClassName={showLabel ? Styles.width80 : ''}
          sourceData={{
            users: workspaceMembers.filter(isNotSelf),
            channels: workspaceChannels,
          }}
          recipients={recipients}
          topGroups={buildTopGroups()}
          className={Styles.recipientSelectorContainer}
          onChange={handleRecipientsSelectorChange}
          disabled={!userCanEdit}
        />
        {accessOptions.length > 0 && (
          <AccessDropdown
            options={accessOptions as DropdownOptions[]}
            currentPermission={selectedPermission}
            disabled={!userCanEdit}
            size="lg"
            variant={accessDropdownVariant}
            handleClick={(permission: ReactText) => setShareSheet({ selectedPermission: permission as Permission })}
          />
        )}
      </div>
      {showSuggestedReviewers && (
        <div className={Styles.secondaryActions}>
          {withSuggestedReviewers && suggestedReviewersShown.length > 0 && (
            <div className={Styles.suggestedReviewers}>
              {suggestedReviewersShown.map((reviewer) => (
                <div
                  key={reviewer.id}
                  className={Styles.suggestedReviewer}
                  onClick={() => {
                    handleRecipientsSelectorChange([
                      ...recipients,
                      { type: RecipientsSelectorValueType.User, value: reviewer },
                    ])
                  }}
                >
                  <Plus />
                  {reviewer.display_name}
                </div>
              ))}
            </div>
          )}
          {secondaryAction}
        </div>
      )}
    </>
  )
}
